import { useState } from 'react';

import type { SortDirection } from '../types';

export type TableSortPriority = number | null;

export type TableFilterValue = any | null;

export type TableSortData<DataIndex extends string> = {
  dataIndex: DataIndex;
  direction: SortDirection | null;
};

export type TableFilterData<DataIndex extends string> = {
  dataIndex: DataIndex;
  value: TableFilterValue;
};

export type TableSortAndFilterState<DataIndex extends string> = {
  sorts: TableSortData<DataIndex>[];
  filters: TableFilterData<DataIndex>[];
};

const possibleSortDirections = [
  [],
  ['ascending'],
  ['descending'],
  ['ascending', 'descending'],
] as const satisfies readonly (readonly SortDirection[])[];

/**
 * Ensures that PossibleSortDirections is an array containing at most the
 * elements 'ascending' and 'descending' in that order.
 */
export type PossibleSortDirections = (typeof possibleSortDirections)[number];

// Internal function.  Get the next sort state based on the available sort directions.
const nextSortDirection = (
  currentDir: SortDirection | null = null,
  possibleDirs: PossibleSortDirections = [],
) => {
  const possibilities = [null, ...possibleDirs];
  const output =
    possibilities[
      possibilities.findIndex((item) => item === currentDir) + 1 >
      possibilities.length - 1
        ? 0
        : [null, ...possibleDirs].findIndex((item) => item === currentDir) + 1
    ];
  return output;
};

/**
 * A utility function that gets the sort direction value for a given `dataIndex` from a provided `sortAndFilterState`.
 * @param {*} dataIndex the column identifer you'd like to get the sort direction of.
 * @param {*} sortAndFilterState the `sortAndFilterState` value you'd like to get sort direction from.
 * @returns a direction string ('ascending' or 'descending) if the column has a sorting direction, or otherwise null.
 */
const getSortDirection = <DataIndex extends string>(
  dataIndex: DataIndex,
  sortAndFilterState: TableSortAndFilterState<DataIndex>,
): SortDirection | null => {
  return (
    sortAndFilterState.sorts.find(
      (sortData) => sortData.dataIndex === dataIndex,
    )?.direction || null
  );
};

/**
 * A utility function that gets the sort priority value for a given `dataIndex` from a provided `sortAndFilterState`.
 * @param {*} dataIndex the column identifer you'd like to get the sort priority of.
 * @param {*} sortAndFilterState the `sortAndFilterState` value you'd like to get sort priority from.
 * @returns a priority number if the column has sorting priority, or otherwise null.
 */
const getSortPriority = <DataIndex extends string>(
  dataIndex: DataIndex,
  sortAndFilterState: TableSortAndFilterState<DataIndex>,
): TableSortPriority => {
  const index: number = sortAndFilterState.sorts.findIndex(
    (sortData) => sortData.dataIndex === dataIndex,
  );

  return index !== -1 ? index + 1 : null;
};

/**
 * A utility function that gets the filter value for a given `dataIndex` from a provided `sortAndFilterState`.
 * @param dataIndex the column identifer you'd like to get the filter value  of.
 * @param sortAndFilterState the `sortAndFilterState` value you'd like to get sort priority from.
 */
const getFilterValue = <DataIndex extends string>(
  dataIndex: DataIndex,
  sortAndFilterState: TableSortAndFilterState<DataIndex>,
): TableFilterValue => {
  return (
    sortAndFilterState.filters.find(
      (filterData) => filterData.dataIndex === dataIndex,
    )?.value || null
  );
};

/**
 * A hook with utilities for standard Table sort and filter behavior.
 * @param initialSortAndFilters.sorts Initial value of array of sort objects.
 * @param initialSortAndFilters.filters Initial value of array of filter objects.
 */
export const useTableSortAndFilter = <DataIndex extends string = string>(
  initialSortAndFilters: TableSortAndFilterState<DataIndex> = {
    sorts: [],
    filters: [],
  },
) => {
  const [sortAndFilters, setSortAndFilters] = useState<
    TableSortAndFilterState<DataIndex>
  >(initialSortAndFilters);

  /**
   * A function that updates the sort direction (and priority automatically) and filter value in state for a given `dataIndex`.
   * Should be run when the "clear" or "apply" buttons of the filter popover are clicked.
   * @param {*} dataIndex The column identifier to update the state for.
   * @param {*} sortDirection The new sort direction to be applied to state.
   * @param {*} filterValue The new filter value to be applied to state.
   */
  const applySortAndFilter = (
    dataIndex: DataIndex,
    sortDirection: SortDirection | null,
    filterValue: TableFilterValue,
  ) => {
    /* SORTING --------------------------------------------------------------------- */
    // Deep copy the current sorts so we can use filter on the copy.
    let tempSorts = [...sortAndFilters.sorts];

    // If an item with this data index already exists in sorts, remove it.
    tempSorts = tempSorts.filter(
      (sortData) => sortData.dataIndex !== dataIndex,
    );

    // If the new sort direction is not null, append the sort state to the end (correctly making it least priority)
    if (sortDirection != null) {
      tempSorts.push({
        dataIndex,
        direction: sortDirection,
      });
    }
    /* SORTING --------------------------------------------------------------------- */

    /* FILTERING ------------------------------------------------------------------- */
    // Deep copy the current filter so we can use filter and push on the copy.
    let tempFilters = [...sortAndFilters.filters];

    // If the new value is null, filter out this dataIndex from state.
    if (filterValue == null) {
      tempFilters = tempFilters.filter(
        (filterData) => filterData.dataIndex !== dataIndex,
      );
    }
    // Else if the new filter value isn't null:
    else {
      // Get index of any current entry with this dataIndex, if it exists.
      const index = tempFilters.findIndex(
        (filterData) => filterData.dataIndex === dataIndex,
      );

      // If there's currently an entry in state for this, update it;
      if (index !== -1) {
        tempFilters[index].value = filterValue;
      }
      // Else if there's not an entry, add it.
      else {
        tempFilters.push({ dataIndex, value: filterValue });
      }
    }
    /* FILTERING ------------------------------------------------------------------- */

    // Set state with modified results.
    setSortAndFilters({
      sorts: tempSorts,
      filters: tempFilters,
    });
  };

  /**
   * A function that increments the sort value to its next possible value (in 'ascending', 'descending', null order) of a column with the given `dataIndex`.
   * Should be run when the sort button in the table header cell is clicked.
   * @param {*} dataIndex The column identifier to increment sort for.
   * @param {*} possibleDirections The allowed sort directions for the provided `dataIndex`.  Should contain at most ['ascending', 'descending'] in that order.
   */
  const incrementSort = (
    dataIndex: DataIndex,
    possibleDirections: PossibleSortDirections,
  ) => {
    // get current direction (will be null if it doesn't yet exist)
    const currentDirection: SortDirection | null =
      sortAndFilters.sorts.find((sortData) => sortData.dataIndex === dataIndex)
        ?.direction || null;

    // get next direction
    const nextDirection = nextSortDirection(
      currentDirection,
      possibleDirections,
    );

    /* SORTING --------------------------------------------------------------------- */
    // Deep copy the current sorts so we can use filter on the copy.
    let tempSorts = [...sortAndFilters.sorts];

    // If an item with this data index already exists in sorts, remove it.
    tempSorts = tempSorts.filter(
      (sortData) => sortData.dataIndex !== dataIndex,
    );

    // If the new sort direction is not null, append the sort state to the end (correctly making it least priority)
    if (nextDirection != null) {
      tempSorts.push({
        dataIndex,
        direction: nextDirection,
      });
    }
    /* SORTING --------------------------------------------------------------------- */

    setSortAndFilters({
      sorts: tempSorts,
      // Don't change filters, since this is just incrementing sorting.
      filters: sortAndFilters.filters,
    });
  };

  return {
    sortAndFilters,
    setSortAndFilters,
    getSortDirection,
    getSortPriority,
    getFilterValue,
    applySortAndFilter,
    incrementSort,
  };
};
